import { environment } from 'environments/environment';

export const API_ROUTES = {
    // Device Management
    AUTH: {
        GET_ALL: 'api/v1/users',
        GET_BY_ID: 'api/v1/users',
        DELETE: 'api/v1/users',
        UPDATE: 'api/v1/users',
        CREATE: 'api/v1/users',
        SET_PASSWORD: (id: string) => `api/v1/users/${id}/set-password`,
        GET_PROFILE: 'api/v1/user/profile',
        PATCH_PROFILE: 'api/v1/user/profile',
        PATCH_PROFILE_PASSWORD: 'api/v1/user/profile/updatepassword',
    },

    GROUPS: {
        GET_ALL: 'api/v1/groups',
        ADD_USER_TO_GROUP: (id: string) => `api/v1/groups/${id}/add-user`,
        REMOVE_USER_FROM_GROUP: (id: string) =>
            `api/v1/groups/${id}/remove-user`,
    },

    DEVICES: {
        GET_ALL: 'api/v1/devices',
        GET_BY_ID: 'api/v1/devices',
        DELETE: 'api/v1/devices',
        UPDATE: 'api/v1/devices',
        CREATE: 'api/v1/devices',
        GET_USERS: (id: string | number) => `api/v1/devices/${id}/users`,
        REMOVE_USER: (id: string | number) =>
            `api/v1/devices/${id}/users/remove`,
        ADD_USER: (id: string | number) => `api/v1/devices/${id}/users/add`,
        GET_API_KEYS: (id: string | number) => `api/v1/devices/${id}/keys`,
        DELETE_API_KEY: (deviceId: string | number, keyId: string | number) =>
            `api/v1/devices/${deviceId}/keys/${keyId}`,
        CREATE_API_KEY: (deviceId: string | number) =>
            `api/v1/devices/${deviceId}/keys/`,
        GET_ALERT_EMAIL: (deviceId: string | number) =>
            `api/v1/devices/${deviceId}/alert`,
        DELETE_ALERT_EMAIL_DEVICE: (
            deviceId: string | number,
            deviceUid: string,
            emailAddress: string
        ) =>
            `api/v1/devices/${deviceId}/device/${deviceUid}/email/${emailAddress}`,
        DELETE_ALERT_EMAIL_PLANT: (
            deviceId: string | number,

            emailAddress: string
        ) => `api/v1/devices/${deviceId}/email/${emailAddress}`,
        PATCH_ALERT_EMAIL: (deviceId: string | number) =>
            `api/v1/devices/${deviceId}/alert/email`,
        PATCH_ALERT_IDLE_TIME: (deviceId: string | number) =>
            `api/v1/devices/${deviceId}/alert/time`,
        ACTIONS: {
            RESTART: (deviceId: string | number) =>
                `api/v1/devices/${deviceId}/actions/restart`,
            UPDATE: (deviceId: string | number) =>
                `api/v1/devices/${deviceId}/actions/update`,
            VPN_START: (deviceId: string | number) =>
                `api/v1/devices/${deviceId}/actions/vpn/start`,
            VPN_STOP: (deviceId: string | number) =>
                `api/v1/devices/${deviceId}/actions/vpn/stop`,
            VPN_STATUS: (deviceId: string | number) =>
                `api/v1/devices/${deviceId}/actions/vpn/status`,
            SET_POINT_SCHEDULE_UPDATE: (deviceId: string | number) =>
                `api/v1/devices/${deviceId}/actions/setpointSchedule/update`,
            GET_ALL: (deviceId: string | number) =>
                `api/v1/devices/${deviceId}/actions/`,
            GET_DETAILS_BY_ID: (id: string | number) =>
                `api/v1/devices/actions/${id}`,
        },
    },

    LOOKUPS: {
        GET_ALL: 'api/v1/lookups',
        GET_BY_NAME: (name: string) => `api/v1/lookups/${name}`,
    },
    VISITED: {
        GET: 'api/v1/visited',
        POST: 'api/v1/visited/device',
    },

    // Application Settings (per microservice: Data Aggregator / Data Collector).
    // Base URL is selected per microservice in the service layer.
    APP_SETTINGS: {
        GET: 'api/v1/settings',
        UPDATE: 'api/v1/settings',
    },

    // Data Collector
    DEVICE_TYPES: {
        GET_ALL: 'api/v1/settings/deviceTypes',
        CREATE: 'api/v1/settings/deviceTypes',
        DELETE: (deviceType: string) =>
            `api/v1/settings/deviceTypes/${deviceType}`,
    },

    DEVICES_DATA_COLLECTOR: {
        GET_META_DATA: (
            sender_uid: string,
            device_uid: string,
            device_type?: string,
            take?: number,
            message_type?: string
        ) => {
            // The type-scoped variant is preferred; fall back to the untyped
            // endpoint when the device type is unknown (e.g. legacy links).
            const base = device_type
                ? `api/v1/sender/${sender_uid}/devices/${device_uid}/type/${encodeURIComponent(device_type)}/live-data/metadata`
                : `api/v1/sender/${sender_uid}/devices/${device_uid}/live-data/metadata`;

            let url = `${base}?take=${take}`;

            if (message_type) {
                url += `&message_type=${message_type}`;
            }

            return url;
        },
        GET_LIVE_DATA_PAYLOAD: (sender_uid: string, device_uid, id: string) =>
            `api/v1/sender/${sender_uid}/devices/${device_uid}/live-data/${id}/payload`,
        GET_EVENT_META_DATA: (
            sender_uid: string,
            device_uid: string,
            device_type?: string,
            take?: number
        ) => {
            // Same as GET_META_DATA: prefer the type-scoped endpoint, fall back
            // to the untyped one when the device type is unknown.
            const base = device_type
                ? `api/v1/sender/${sender_uid}/devices/${device_uid}/type/${encodeURIComponent(device_type)}/event-data/metadata`
                : `api/v1/sender/${sender_uid}/devices/${device_uid}/event-data/metadata`;

            return `${base}?take=${take}`;
        },
        GET_EVENT_DATA_PAYLOAD: (
            sender_uid: string,
            device_uid: string,
            id: string
        ) =>
            `api/v1/sender/${sender_uid}/devices/${device_uid}/event-data/${id}/payload`,
    },

    BIFI_SYSTEM_HEALTH: {
        GET_SYSTEM_HEALTH: (bifiId: string, take: number) =>
            `api/v1/system-health/${bifiId}?take=${take} `,
    },

    // Data Aggregator
    AGGREGATED_PROPERTIES: {
        GET_ALL: 'api/v1/settings/aggregatedProperties',
        CREATE: 'api/v1/settings/aggregatedProperties',
        DELETE: (propertyName: string) =>
            `api/v1/settings/aggregatedProperties/${propertyName}`,
    },

    PROPERTY_MAPPINGS: {
        GET_ALL: 'api/v1/propertyMappings',
        GET_BY_ID: (id: string) => `api/v1/propertyMappings/${id}`,
        CREATE: 'api/v1/propertyMappings',
        UPDATE: (id: string) => `api/v1/propertyMappings/${id}`,
        DELETE: (id: string) => `api/v1/propertyMappings/${id}`,
    },

    ALARM_DEFINITIONS: {
        GET_ALL: 'api/v1/settings/alertDefinitions',
        GET_BY_ID: (id: string) => `api/v1/settings/alertDefinition/${id}`,
        CREATE: 'api/v1/settings/alertDefinitions',
        UPDATE: (id: string) => `api/v1/settings/alertDefinition/${id}`,
        DELETE: (id: string) => `api/v1/settings/alertDefinitions/${id}`,
    },
    ALARM_STATE: {
        // The type-scoped variants are v2 (not v1) and use `types` (plural);
        // each falls back to the v1 path when the device type is unknown.
        GET_CURRENT: (
            sender_uid: string,
            device_uid: string,
            device_type?: string
        ) =>
            device_type
                ? `api/v2/sender/${sender_uid}/devices/${device_uid}/types/${encodeURIComponent(device_type)}/alarmstates/current`
                : `api/v1/sender/${sender_uid}/devices/${device_uid}/alarmstates/current`,
        GET_STATES: (
            sender_uid: string,
            device_uid: string,
            device_type?: string
        ) =>
            device_type
                ? `api/v2/sender/${sender_uid}/devices/${device_uid}/types/${encodeURIComponent(device_type)}/alarmstates`
                : `api/v1/sender/${sender_uid}/devices/${device_uid}/alarmstates`,
        GET_CUMULATIVE: (
            sender_uid: string,
            device_uid: string,
            device_type?: string
        ) =>
            device_type
                ? `api/v2/sender/${sender_uid}/devices/${device_uid}/types/${encodeURIComponent(device_type)}/alarmstates/cumulative`
                : `api/v1/sender/${sender_uid}/devices/${device_uid}/alarmstates/cumulative`,
        // Gateway-level, not device-scoped - no v2 variant published.
        GET_HEADER_CURRENT_: (sender_uid: string) =>
            `api/v1/sender/${sender_uid}/alarmstates/current`,
    },
    PERSONALIZED_ALARMS: {
        GET_ALL: 'api/v1/personalizedAlarms',
        GET_BY_ID: (id: string) => `api/v1/personalizedAlarms/${id}`,
        UPDATE: (id: string) => `api/v1/personalizedAlarms/${id}`,
        CREATE: 'api/v1/personalizedAlarms',
        DELETE: (id: string) => `api/v1/personalizedAlarms/${id}`,
        DEACTIVATE: (id: string) =>
            `api/v1/personalizedAlarms/${id}/deactivate`,
        ACTIVATE: (id: string) => `api/v1/personalizedAlarms/${id}/activate`,
        PAUSE: (id: string) => `api/v1/personalizedAlarms/${id}/pause`,
        GET_DEVICES_BY_USERID: (userId: string) =>
            `api/v1/users/${userId}/devices`,
        GET_DEVICES_BY_SENDERUID: (sender_uid: string) =>
            `api/v1/sender/${sender_uid}/devices`,
        GET_PERSONALIZED_ALARM_NOTIFICATIONS: (id: string) =>
            `api/v1/personalizedAlarms/${id}/notifications`,
        GET_PERSONALIZED_ALARM_NOTIFICATIONS_MESSAGES_BY_ID: (
            id: string,
            notificationId: string
        ) =>
            `api/v1/personalizedAlarms/${id}/notifications/${notificationId}/messages`,
    },

    AGGREGATED_LIVE_DATA: {
        GET_MIN: (sender_uid: string, device_uid: string) =>
            `api/v1/aggregated/${sender_uid}/${device_uid}/min`,
        GET_MAX: (sender_uid: string, device_uid: string) =>
            `api/v1/aggregated/${sender_uid}/${device_uid}/max`,
        GET_AVG: (sender_uid: string, device_uid: string) =>
            `api/v1/aggregated/${sender_uid}/${device_uid}/avg`,
        GET_LAST: (sender_uid: string, device_uid: string) =>
            `api/v1/aggregated/${sender_uid}/${device_uid}/last`,
    },

    EVENT_DATA: {
        GET_ALL: 'api/v1/eventData',
        GET_BY_ID: (id: string) => `api/v1/eventData/${id}`,
        END: (id: string) => `api/v1/eventData/${id}/end`,
    },

    AGGREGETED_DEVICE: {
        GET_ALL_BY_ID: (sender_uid) => `api/v2/sender/${sender_uid}/devices`,
        REMOVE: (sender_uid) => `api/v1/sender/${sender_uid}/devices/remove`,
        // NOTE: this one is `types` (plural) + `/details`, unlike the
        // `type` (singular) segment used by the data-collector routes above.
        GET_BY_DEVICEUID: (
            sender_uid: string,
            device_uid: string,
            device_type?: string
        ) =>
            device_type
                ? `api/v1/sender/${sender_uid}/devices/${device_uid}/types/${encodeURIComponent(device_type)}/details`
                : `api/v1/sender/${sender_uid}/devices/${device_uid}`,
    },

    HEALTH_CHECK: {
        AGGREGATOR: environment.appSettings.endpoints.healthCheck.aggregator,
        DEVICE_MANAGEMENT:
            environment.appSettings.endpoints.healthCheck.deviceManagement,
        DATA_COLLECTOR:
            environment.appSettings.endpoints.healthCheck.dataCollector,
    },

    // Databricks token service. Exchanges the caller's Keycloak bearer for a
    // short-lived Databricks token scoped to the embedded dashboard.
    DATABRICKS: {
        GET_TOKEN: 'api/v1/Token',
    },

    EVENT_ALARM_EMAILS: {
        GET_ALL: (deviceId: string | number) =>
            `api/v1/devices/${deviceId}/eventAlarms/emails`,
        CREATE: (deviceId: string | number) =>
            `api/v1/devices/${deviceId}/eventAlarms/emails`,
        DELETE: (deviceId: string | number, email: string) =>
            `api/v1/devices/${deviceId}/eventAlarms/emails/${email}`,
    },
};
